import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { fetchSessions, revokeUserSessions, revokeAllSessions } from '@/api/admin.api';
import type { SessionInfo } from '@genealogy-app/shared';

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'gerade eben';
  if (m < 60) return `vor ${m} Min.`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} Std.`;
  return `vor ${Math.floor(h / 24)} Tag(en)`;
}

function parseUA(ua: string | null): string {
  if (!ua) return 'Unbekannt';
  if (/iPhone|iPad/.test(ua)) return '📱 iOS';
  if (/Android/.test(ua)) return '📱 Android';
  if (/Windows/.test(ua)) return '🖥 Windows';
  if (/Macintosh|Mac OS X/.test(ua)) return '🍎 macOS';
  if (/Linux/.test(ua)) return '🐧 Linux';
  return '🌐 Browser';
}

export function AdminSessionsPage() {
  const queryClient = useQueryClient();
  const [confirmAll, setConfirmAll] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'sessions'],
    queryFn: fetchSessions,
    refetchInterval: 30_000,
  });

  const revokeUserMutation = useMutation({
    mutationFn: revokeUserSessions,
    onSuccess: (_, userId) => {
      toast.success('Sitzungen des Benutzers widerrufen.');
      queryClient.setQueryData<SessionInfo[]>(['admin', 'sessions'], (old) =>
        old?.filter((s) => s.userId !== userId) ?? [],
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const revokeAllMutation = useMutation({
    mutationFn: revokeAllSessions,
    onSuccess: () => {
      setConfirmAll(false);
      toast.success('Alle Sitzungen widerrufen.');
      queryClient.invalidateQueries({ queryKey: ['admin', 'sessions'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Sitzungen nach Benutzer gruppieren
  const grouped = (data ?? []).reduce<Map<string, { email: string; sessions: SessionInfo[] }>>(
    (map, session) => {
      const entry = map.get(session.userId) ?? { email: session.userEmail, sessions: [] };
      entry.sessions.push(session);
      map.set(session.userId, entry);
      return map;
    },
    new Map(),
  );

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 shadow-sm">
        <div className="flex items-center gap-2 sm:gap-4">
          <h1 className="text-base font-semibold text-slate-800 sm:text-lg">Sitzungsverwaltung</h1>
        </div>

        {data && data.length > 0 && (
          <div>
            {!confirmAll ? (
              <button
                onClick={() => setConfirmAll(true)}
                className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                Alle widerrufen
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-700">Alle Sitzungen widerrufen?</span>
                <button
                  onClick={() => revokeAllMutation.mutate()}
                  disabled={revokeAllMutation.isPending}
                  className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {revokeAllMutation.isPending ? 'Widerrufe…' : 'Ja'}
                </button>
                <button
                  onClick={() => setConfirmAll(false)}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Nein
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto max-w-3xl space-y-4">
          {isLoading && (
            <div className="flex justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
            </div>
          )}
          {isError && (
            <p className="text-center text-sm text-red-500">Fehler beim Laden der Sitzungen.</p>
          )}
          {data && data.length === 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
              <p className="text-slate-500">Keine aktiven Sitzungen.</p>
            </div>
          )}

          {Array.from(grouped.entries()).map(([userId, { email, sessions }]) => (
            <div key={userId} className="rounded-lg border border-slate-200 bg-white shadow-sm">
              {/* E-Mail-Adressen sind lang; am Handy stünde der Knopf sonst
                  außerhalb des Bildschirms. Deshalb untereinander. */}
              <div className="flex flex-col gap-2 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-2">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
                    {email.charAt(0).toUpperCase()}
                  </div>
                  <span className="truncate font-medium text-slate-900">{email}</span>
                  <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                    {sessions.length} Sitzung{sessions.length !== 1 ? 'en' : ''}
                  </span>
                </div>
                <button
                  onClick={() => revokeUserMutation.mutate(userId)}
                  disabled={revokeUserMutation.isPending}
                  className="shrink-0 self-start rounded-md border border-red-200 bg-white px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 sm:self-auto"
                >
                  Alle widerrufen
                </button>
              </div>
              <div className="divide-y divide-slate-50">
                {sessions.map((s) => (
                  <div key={s.id} className="flex flex-col gap-1 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-base">{parseUA(s.userAgent).split(' ')[0]}</span>
                      <div>
                        <p className="font-medium text-slate-800">{parseUA(s.userAgent)}</p>
                        <p className="text-xs text-slate-500">
                          {s.ipAddress ?? 'IP unbekannt'} · Zuletzt aktiv {formatRelative(s.lastSeenAt)}
                        </p>
                      </div>
                    </div>
                    <span className="pl-9 text-xs text-slate-400 sm:pl-0">
                      seit {new Date(s.createdAt).toLocaleDateString('de-DE')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export default AdminSessionsPage;
